import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);

  useSeo({
    title: "搜索结果 - 呱呱视频社区",
    description: "查找呱呱视频社区提供的各类视频内容，快速定位感兴趣视频。",
    keywords: "视频搜索,呱呱视频社区,搜索视频,短视频搜索",
  });

  const results = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    return videos.filter((v) => {
      const matchKeyword =
        v.title.toLowerCase().includes(q) ||
        v.tags.some((tag) => tag.toLowerCase().includes(q)) ||
        v.author.toLowerCase().includes(q);
      const matchCategory = category === "all" || v.category === category;
      return matchKeyword && matchCategory;
    });
  }, [keyword, category]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = results.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  const onCategory = (slug: string) => {
    setCategory(slug);
    setPage(1);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
      <Breadcrumb items={[{ label: "搜索结果" }]} />

      <div className="mt-5">
        <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">搜索视频</h1>
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex items-center gap-2 h-11 px-4 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-400 flex-1">
            <i className="ri-search-line text-foreground-400" aria-hidden="true"></i>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如：露营、美食、搞笑"
              className="flex-1 bg-transparent outline-none text-sm"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </div>

      {keyword && (
        <p className="mt-6 text-sm text-foreground-600">
          关于 “<span className="text-primary-600 font-semibold">{keyword}</span>” 的结果，
          共找到 <span className="text-primary-600 font-semibold">{results.length}</span> 个视频
        </p>
      )}

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onCategory("all")}
          className={`h-8 px-4 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors ${
            category === "all" ? "bg-primary-500 text-background-50 font-semibold" : "bg-background-100 text-foreground-600 hover:text-primary-600"
          }`}
        >
          全部分类
        </button>
        {categories.map((cat) => (
          <button
            key={cat.slug}
            type="button"
            onClick={() => onCategory(cat.slug)}
            className={`h-8 px-4 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors ${
              category === cat.slug ? "bg-primary-500 text-background-50 font-semibold" : "bg-background-100 text-foreground-600 hover:text-primary-600"
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {!keyword ? (
          <EmptyState
            title="输入关键词开始搜索"
            description="试试搜索你感兴趣的内容，比如“露营”“搞笑短剧”“早餐”。"
          />
        ) : pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {pageItems.map((v) => (
                <VideoCard
                  key={v.id}
                  id={v.id}
                  title={v.title}
                  cover={v.cover}
                  author={v.author}
                  category={v.category}
                  views={v.views}
                  duration={v.duration}
                  publishDate={v.publishDate}
                  authorType={v.authorType}
                />
              ))}
            </div>
            <div className="mt-8">
              <Pagination page={current} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <EmptyState
            title="没有找到相关视频"
            description={`没有与“${keyword}”匹配的内容，换一个关键词，或者去首页看看热门推荐。`}
            actionLabel="重置搜索"
            onAction={() => {
              setInput("");
              setCategory("all");
              setParams({});
            }}
          />
        )}
      </div>
    </div>
  );
}